import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

export type Sugerencia = components['schemas']['SugerenciaRead']

type ConfirmarResult = components['schemas']['ConfirmarResult']

export const SUGERIDO_KIND_LABEL_KEY: Record<Sugerencia['backed_by_kind'], string> = {
  ipevr: 'marcado.pendientes.kind.ipevr',
  perfil: 'marcado.pendientes.kind.perfil',
  constructores: 'marcado.pendientes.kind.constructores',
  proveedores: 'marcado.pendientes.kind.proveedores',
}

export const PENDIENTES_PAGE_SIZE = 20

export function usePendientes(businessId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['sgsst-pendientes', businessId, page],
    queryFn: async () =>
      (
        await api.get<Page<Sugerencia>>(`/businesses/${businessId}/sgsst/pendientes`, {
          params: {page, page_size: PENDIENTES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

// Estándares confirmados cuyo respaldo cambió después.
export function useReconfirmacion(businessId: string | undefined, page = 1) {
  return useQuery({
    queryKey: ['sgsst-reconfirmacion', businessId, page],
    queryFn: async () =>
      (
        await api.get<Page<Sugerencia>>(`/businesses/${businessId}/sgsst/pendientes/reconfirmacion`, {
          params: {page, page_size: PENDIENTES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

export type SoporteVencimiento = components['schemas']['SoporteVencimientoRead']

// Trae también los dos conteos de todo el negocio: el número de cada chip no se mueve al filtrar.
export type SoportesVencimientoPage = components['schemas']['SoportesVencimientoPage']

// `undefined` es «Todos».
export type SoporteVencimientoFiltro = 'vencido' | 'vence_pronto'

export function useSoportesPorVencer(businessId: string | undefined, page = 1, estado?: SoporteVencimientoFiltro) {
  return useQuery({
    queryKey: ['soportes-vencimientos', businessId, page, estado],
    queryFn: async () =>
      (
        await api.get<SoportesVencimientoPage>(`/businesses/${businessId}/sgsst/pendientes/soportes`, {
          params: {page, page_size: PENDIENTES_PAGE_SIZE, estado},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

export function useConfirmarPendientes(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await api.post<ConfirmarResult>(`/businesses/${businessId}/sgsst/pendientes/confirmar`, {ids})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['sgsst-pendientes', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst-reconfirmacion', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
    },
  })
}
